import {
  useDeleteJobPostMutation,
  useGetAllJobPostsQuery,
} from "@/app/features/recruitment/job-applicants/job-applicants.api";
import ConfirmationModal from "@/components/ConfirmationModal";
import CustomTable from "@/components/CustomTable";
import StatusTag from "@/components/StatusTag";
import { DISCLOSER_KEYS, FILTER_KEYS } from "@/constants/keys.constants";
import { useAppDiscloser, useAppFilters } from "@/hooks";
import { AiFileIcon, EyeIcon, PencilEdit02Icon, TrashIcon } from "@/icons";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import { formatDate, normalizeColumnValue } from "@/utils";
import { Button, Space, Tooltip, type TableProps } from "antd";
import { useSearchParams } from "react-router";
import JobPostDetails from "./_components/JobPost/JobPostDetails";
import ManageJobPost from "./_components/JobPost/ManageJobPost";
import InterviewQuestions from "./_components/JobPost/InterviewQuestions";

const JOP_POST_SHEET_STATUS_CONFIG = [
  {
    label: "Published",
    value: "PUBLISHED",
    className: "status status-success",
  },
  { label: "Draft", value: "DRAFT", className: "status status-yellow" },
  { label: "Closed", value: "CLOSED", className: "status status-error" },
];

export default function JobPost({ setSegment }: { setSegment: any }) {
  const [, setParams] = useSearchParams();
  const { page, limit, skip, filters, keyword, handlePagination } =
    useAppFilters(FILTER_KEYS.JOB_POST);
  const [config, handleOpen, handleClose] = useAppDiscloser<"AI_DETAILS">(
    DISCLOSER_KEYS.MANAGE_JOB_POST,
  );

  // API call to get all job posts
  const { data, isLoading, isFetching } = useGetAllJobPostsQuery({
    params: {
      skip,
      limit,
      ...filters,
      search: keyword,
    },
  });

  const [deleteJobPost, { isLoading: isDeleting }] = useDeleteJobPostMutation();

  const handleDelete = async () => {
    try {
      if (!config?.data?.id) return;
      const response = await deleteJobPost({ id: config.data.id }).unwrap();
      if ((response as any)?.success === false) {
        toast("error", getErrorMessage(response, "Failed to delete job post"));
        return;
      }
      handleClose();
      toast("success", "Job post deleted successfully");
    } catch (error) {
      toast("error", getErrorMessage(error, "Failed to delete job post"));
    }
  };

  const columns: TableProps<any>["columns"] = [
    {
      title: "ID",
      dataIndex: "job_code",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },

    {
      title: "Employee Type",
      dataIndex: "employee_type",
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },

    {
      title: "Published On",
      dataIndex: "publish_date",
      align: "center",
      render: (val) => formatDate(val),
    },
    {
      title: "Hiring Date",
      dataIndex: "hiring_date",
      align: "center",
      render: (val) => formatDate(val),
    },
    {
      title: "Assigned Recruiter",
      dataIndex: "evaluator_name",
      align: "center",
      render: (val) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Salary Range",
      dataIndex: "salary_range",
      render: (_val, record) => {
        if (record.min_salary && record.max_salary) {
          return `BDT ${record.min_salary} - ${record.max_salary}`;
        }
        return "N/A";
      },
    },
    {
      title: "Total Vacancies",
      dataIndex: "vacancy",
      render: (val) => normalizeColumnValue(val, 0),
    },
    {
      title: "Total Applicants",
      align: "center",
      render: (record) => (
        <span>
          {record.total_applicant !== null && record.total_applicant > 0 ? (
            <Button
              type="default"
              onClick={() => {
                setParams({ job_id: record.id });
                setSegment("applicants");
              }}
              className="text-[#00C6AE] border-none !bg-transparent shadow-none
              underline underline-offset-4 decoration-[#00C6AE] p-0"
            >
              {record.total_applicant}
            </Button>
          ) : (
            <div>0</div>
          )}
        </span>
      ),
    },
    {
      title: "Short Listed",
      dataIndex: "total_short_listed",
      align: "center",
      render: (val, record) => (
        <span>
          {val !== null && val > 0 ? (
            <Button
              type="default"
              onClick={() => {
                setParams({ job_id: record.id });
                setSegment("shortlisted");
              }}
              className="text-[#00C6AE] border-none !bg-transparent shadow-none
              underline underline-offset-4 decoration-[#00C6AE] p-0"
            >
              {val}
            </Button>
          ) : (
            <div>0</div>
          )}
        </span>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      render: (status: string) => (
        <Tooltip
          title={
            status === "DRAFT"
              ? `Click 'Sync Job Status' on the scheduled date to publish.`
              : ""
          }
        >
          <span>
            <StatusTag value={status} config={JOP_POST_SHEET_STATUS_CONFIG} />
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      align: "end",
      render: (record: any) => {
        const isDisabled =
          (record.total_applicant !== null && record.total_applicant > 0) ||
          record.status === "CLOSED";

        const tooltipTitle =
          record.status === "CLOSED"
            ? "Job post is closed"
            : "Already have applied applicants";

        return (
          <Space size={8}>
            <Button
              size="small"
              className="action-btn"
              icon={<EyeIcon size={20} />}
              onClick={() =>
                handleOpen({ type: "VIEW", data: { id: record.id } })
              }
            />

            <Tooltip title={isDisabled ? tooltipTitle : ""}>
              <Button
                size="small"
                className="action-btn disabled:!opacity-50"
                icon={<PencilEdit02Icon size={22} />}
                disabled={isDisabled}
                onClick={() =>
                  handleOpen({ type: "UPDATE", data: { id: record.id } })
                }
              />
            </Tooltip>

            <Tooltip title={isDisabled ? tooltipTitle : ""}>
              <Button
                size="small"
                className="action-btn disabled:!opacity-50"
                icon={<TrashIcon size={20} />}
                disabled={isDisabled}
                onClick={() =>
                  handleOpen({ type: "DELETE", data: { id: record.id } })
                }
              />
            </Tooltip>
            <Button
              size="small"
              className="action-btn"
              icon={<AiFileIcon size={20} />}
              onClick={() =>
                handleOpen({ type: "AI_DETAILS", data: { id: record.id } })
              }
            />
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        loading={isLoading || isFetching}
        customTitle="Job Post"
        tableRootClassName="border-0 p-0"
        onPaginate={handlePagination}
        dataSource={data?.data?.data || []}
        total={data?.data?.meta?.total || 0}
      />

      <ManageJobPost
        id={config.data?.id}
        handleClose={handleClose}
        open={config.open && ["ADD", "UPDATE"].includes(config?.type || "")}
      />
      <JobPostDetails
        id={config.data?.id}
        handleClose={handleClose}
        open={config.open && config.type === "VIEW"}
      />
      <ConfirmationModal
        open={config.open && config.type === "DELETE"}
        handleClose={() => handleClose()}
        handleSubmit={() => handleDelete()}
        title="Action Required"
        subTitle="Are you sure, you want to delete this job post?"
        loading={isDeleting}
        buttonName="Delete"
      />
      <InterviewQuestions
        open={config.open && config.type === "AI_DETAILS"}
        handleClose={() => handleClose()}
        id={config.data?.id}
      />
    </div>
  );
}
